import { useCallback } from "react";
import jwt_decode from "jwt-decode";
import { useHistory } from "react-router-dom";
import { JsonValue, Message } from "@bufbuild/protobuf";
import { datadogRum } from "@datadog/browser-rum";

import {
  ACCESS_TOKEN_KEY,
  AUTH_URL,
  EXPIRES_AT_KEY,
  LAST_LOGIN_AT_KEY,
  LOGIN_STATE_SENTINEL,
  ORGANIZATION_KEY,
  RETURN_PATH_KEY,
  SESSION_CONTENTS,
} from "@shared/constants";
import { ApiError, AuthType, UserData } from "@shared/types";
// TODO: there's a circular dependency here somewhere, pointing to the file directly fixes the issue
import { recoverBrowserSecurityError } from "@shared/utils/lib/storage/storage";
import { decodeJwtIdentity } from "@shared/utils/lib/userUtils";

import { checkStatus } from "../fetch";

interface IAuthenticateRequest {
  redirectUri: string;
  code: string;
  state: string | null;
}

interface AuthHeaders {
  Authorization: string;
}

export enum AuthStates {
  Unauthorized = "UNAUTHORIZED",
  Authorized = "AUTHORIZED",
}

const CONTENT_TYPE_JSON = { "Content-Type": "application/json" };

export function getAuthBaseURL() {
  return AUTH_URL;
}

function clearTokens(all?: boolean) {
  recoverBrowserSecurityError(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(EXPIRES_AT_KEY);
    localStorage.removeItem(ORGANIZATION_KEY);
    if (all) {
      localStorage.removeItem(LAST_LOGIN_AT_KEY);
      localStorage.removeItem(LOGIN_STATE_SENTINEL);
      localStorage.removeItem(SESSION_CONTENTS);
    }
  });
}

export async function checkAuthorized(response: Response) {
  if (response.status === 401) {
    clearTokens();
    const err = new ApiError(
      `Authorization failed: ${response.statusText}`,
      response.status
    );
    datadogRum.addError(err);
    window.location.reload();
    throw err;
  } else if (response.status === 403) {
    // Session is valid, but not authorized for this action.
    let message = `Authorization failed: ${response.statusText}`;
    const body = await response.clone().json();
    try {
      if (body?.error) {
        message = `${response.statusText} ${body?.error}`;
      }
    } catch {
      // not valid json in the servers response, move on
    }
    throw new ApiError(message, response.status);
  } else if (
    response.status === 422 &&
    (await response.clone().json()).msg === "Unrecognized JWT key ID"
  ) {
    // Session is valid, but not for this server
    clearTokens();
    throw new ApiError("Unrecognized JWT key ID", response.status);
  } else {
    // Response is authorized
    return response;
  }
}

const codeToBearer = async (
  loginInfo: IAuthenticateRequest,
  authProviderName: string = "github"
): Promise<string> => {
  const code = loginInfo.code;
  const state = loginInfo.state;
  const redirectUri = loginInfo.redirectUri;
  let data: {
    code: string;
    redirectUri: string;
    authProviderName: string;
    state?: string;
  } = {
    code,
    redirectUri,
    authProviderName,
  };
  if (state) {
    data = { ...data, state };
  }
  return fetch(`${getAuthBaseURL()}/authenticate`, {
    method: "post",
    headers: {
      Accept: "application/json, */*",
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
    },
    credentials: "same-origin",
    body: new URLSearchParams(data),
  })
    .then(checkAuthorized)
    .then((res) => res.json())
    .then((data) => data.token);
};

// NOTE(zz): This function is a bit misleading as we only check the value of a timestamp
// which we assume we derived from the actual token and the value has not been tampered with.
// This is a weak assumption and should be revisited.
export function isAuthenticated(): boolean {
  const expiresAt = JSON.parse(
    recoverBrowserSecurityError(() => localStorage.getItem(EXPIRES_AT_KEY)) ||
      "0"
  );
  return new Date().getTime() < expiresAt;
}

export function everAuthenticated(): boolean {
  return (
    recoverBrowserSecurityError(() =>
      localStorage.getItem(LAST_LOGIN_AT_KEY)
    ) !== null
  );
}

export function getAccessToken(): string {
  const accessToken = recoverBrowserSecurityError(() =>
    localStorage.getItem(ACCESS_TOKEN_KEY)
  );
  if (!accessToken) {
    throw new Error("No access token found");
  }
  return accessToken;
}

export function buildAuthorizationHeader(): AuthHeaders {
  return {
    Authorization: `Bearer ${getAccessToken()}`,
  };
}

export async function authRequestRaw<B>(
  method: string,
  path: string,
  body?: B,
  headers?: HeadersInit
) {
  const hasBody = body !== undefined;
  const sendHeaders: HeadersInit = {
    Accept: "application/json",
    ...headers,
    ...(isAuthenticated() ? buildAuthorizationHeader() : {}),
    ...(hasBody ? CONTENT_TYPE_JSON : {}),
  };

  let pathPrefix = "";
  // TODO: remove this once we have a better way to test the frontend
  // Node doesn't have the concept of a location origin, so we need to explicitly set localhost:3000 on every api path
  if (
    import.meta.env.SG_TEST_ENV === "true" ||
    process.env.SG_TEST_ENV === "true" // process is undefined with Vite
  ) {
    pathPrefix = "http://localhost:3000";
  }
  const fullPath = pathPrefix + path;
  try {
    const response = await fetch(fullPath, {
      method: method,
      headers: sendHeaders,
      credentials: "same-origin",
      ...(hasBody ? { body: JSON.stringify(body) } : {}),
    });
    return await checkAuthorized(response);
  } catch (e) {
    if (e instanceof ApiError) {
      // e.g. `ApiError: Authorization failed: 401`
      throw e;
    } else if (e instanceof TypeError) {
      // e.g. `TypeError: Failed to fetch`
      throw e;
    }
    // Fallback case for unknown errors
    throw e;
  }
}

/**
 * Returns a promise that resolves to a {@link Response} object without explicitly converting it to JSON.
 * @param method The HTTP method to use
 * @param path The path to the resource
 * @param body The body of the request
 * @param headers overrides to the {@link HeadersInit} object that is sent with the request
 * @returns
 */
export function authWithResponse<B>(
  method: string,
  path: string,
  body: B,
  headers?: HeadersInit
): Promise<Response> {
  return authRequestRaw<B>(method, path, body, headers)
    .then(checkStatus)
    .then((res) => res);
}

export function authGet<R>(path: string): Promise<R> {
  return authRequestRaw("get", path)
    .then(checkStatus)
    .then((res) => res.json());
}

// Allows an authenticated GET request to be made without throwing an error
// if the request does not return a 2xx status code (e.g. 404 response)
export async function authGetUnchecked<R>(path: string): Promise<R | null> {
  const res = await authRequestRaw("get", path);
  if (res.status >= 200 && res.status < 300) {
    return res.json();
  }
  return Promise.resolve(null);
}

export function authGetText(path: string): Promise<string> {
  return authRequestRaw("get", path)
    .then(checkStatus)
    .then((res) => res.text());
}

export function authPatch<B, R>(path: string, body: B): Promise<R> {
  return authRequestRaw<B>("PATCH", path, body)
    .then(checkStatus)
    .then((res) => res.json());
}

export function authPost<B, R>(path: string, body: B): Promise<R> {
  return authRequestRaw<B>("post", path, body)
    .then(checkStatus)
    .then((res) => res.json());
}

export function authPut<B, R>(path: string, body: B): Promise<R> {
  return authRequestRaw<B>("put", path, body)
    .then(checkStatus)
    .then((res) => res.json());
}

export function authDelete<R>(path: string): Promise<R> {
  return authRequestRaw("delete", path)
    .then(checkStatus)
    .then((res) => res.json());
}

export function authDeleteWithBody<B, R>(path: string, body: B): Promise<R> {
  return authRequestRaw<B>("delete", path, body)
    .then(checkStatus)
    .then((res) => res.json());
}

/**
 * Returns a promise that resolves to a response message.
 * Use this if your request body and response are defined by protobufs. It will automatically serialize and deserialize them correctly.
 * @param method The HTTP method to use
 * @param path The path to the resource
 * @param body The body of the request, should be a protobuf message
 * @param ResponseMessageClass The message class of the response, used for deserializing
 * @returns
 */
export function authPostProto<
  TBody extends Message<TBody>,
  TResponse extends Message<TResponse>
>(
  path: string,
  body: TBody,
  ResponseMessageClass: {
    fromJson(jsonValue: JsonValue): TResponse;
  }
): Promise<TResponse> {
  return authPost<JsonValue, JsonValue>(path, body.toJson()).then((response) =>
    ResponseMessageClass.fromJson(response)
  );
}

const addReturnPathToUrl = (
  targetUrlString: string,
  preferredReturnPath?: string
): string => {
  const currentUrl = new URL(window.location.toString());
  const targetUrl = new URL(targetUrlString, window.location.origin);
  const newReturnPath =
    // reuse existing return path if it exists
    currentUrl.searchParams.get(RETURN_PATH_KEY) ??
    // use requested return path if it exists
    preferredReturnPath ??
    // use current path otherwise
    `${currentUrl.pathname}${currentUrl.search}`;
  targetUrl.searchParams.set(RETURN_PATH_KEY, newReturnPath);
  return `${targetUrl.pathname}${targetUrl.search}`;
};

export const useAuth = () => {
  const history = useHistory();
  const login = useCallback(
    async (
      authProviderName: string,
      authProviderType: AuthType,
      preferredReturnPath?: string
    ) => {
      const targetUrl =
        authProviderType === "saml2"
          ? `/api/auth/saml/login/${authProviderName}`
          : `/api/auth/oauth/login/${authProviderName}`;
      const targetPath = addReturnPathToUrl(targetUrl, preferredReturnPath);
      window.location.href = targetPath; // nosemgrep: should-redirect-with-react-router because the redirect URL doesn't exist in the frontend app
    },
    []
  );

  const scmInstallRedirect = useCallback(
    async (authProviderType: AuthType, preferredReturnPath?: string) => {
      // NOTE (zz): The following line is no longer true as we currently don't set the organization data in localStorage:
      // Allows us to fall back to using the newest org when this function returns
      recoverBrowserSecurityError(() =>
        localStorage.removeItem(ORGANIZATION_KEY)
      );
      // NOTE (zz): Coupling with `Onboarding.tsx` router to redirect to the correct page
      const targetPath =
        authProviderType && authProviderType === "gitlab"
          ? "/gitlab/organizations"
          : "/onboarding/scm/github/install";
      const targetUrl = addReturnPathToUrl(targetPath, preferredReturnPath);
      history.push(targetUrl);
    },
    [history]
  );

  const logout = () => {
    // Allows us to fall back to using the newest org when this function returns
    clearTokens();
    // refresh page so user changes are reflected
    window.location.reload();
  };

  return { login, logout, scmInstallRedirect };
};

export const logoutBack = () => {
  clearTokens();
  window.history.back();
};

/**
 * Sets access token and relevant data to localStorage. Also clears the
 * organization data, since the user may have changed organizations and the new
 * token may not be valid for the old organization.
 * @param token access token to save in localStorage
 */
export const setAccessToken = (token: string) => {
  // nosemgrep: react-jwt-in-localstorage
  const decoded = jwt_decode<any>(token);
  recoverBrowserSecurityError(() => {
    localStorage.removeItem(ORGANIZATION_KEY);
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.setItem(LAST_LOGIN_AT_KEY, new Date().getTime().toString());
    localStorage.setItem(EXPIRES_AT_KEY, JSON.stringify(decoded.exp * 1000)); // nosemgrep: react-jwt-decoded-property
  });
};

export const finishUserAuth = async (
  redirectUri: string,
  code: string,
  state: string | null,
  authProviderName: string = "github",
  useExistingAuth: boolean = false
): Promise<void> => {
  // post code to backend and do auth there;
  const token = useExistingAuth
    ? await authPost<any, { token: string }>(
        `${getAuthBaseURL()}/authenticate_extension`,
        {
          code,
          state,
          redirectUri,
          authProviderName,
        }
      ).then((res) => res.token)
    : await codeToBearer({ redirectUri, code, state }, authProviderName);

  // save bearer token from backend to localStorage
  setAccessToken(token);
};

export const finishSamlAuth = async (token: string): Promise<void> => {
  // TODO drew switch to single-use code in the url param

  // save bearer token from backend to localStorage
  setAccessToken(token);
};

export const getUserData = (): UserData => {
  const token = getAccessToken();
  return decodeJwtIdentity(token);
};

export const getAuthenticationState = (): AuthStates => {
  // All users must be properly authenticated and have a valid user id.
  if (isAuthenticated()) {
    return AuthStates.Authorized;
  } else {
    return AuthStates.Unauthorized;
  }
};
